import { type MouseEvent, useEffect, useMemo, useState } from 'react';
import { css, type Theme } from '@emotion/react';
import { transparentize } from 'polished';
import { ArrowSquareOut, Code, Copy, Info, Plus, TerminalWindow } from '@phosphor-icons/react';
import { useDeploymentClientState } from 'client/state/DeploymentClientState';
import { Button } from 'client/components/Button';
import { DropdownMenu } from 'client/components/DropdownMenu';
import { createStackBackgroundUri } from 'client/components/Icons/StackBackground';
import { Stack } from 'client/components/Stack';
import { Tooltip } from 'client/components/Tooltip';
import { Typography } from 'client/components/Typography';
import { SshWarningDropdown } from '../SshWarningDropdown';
import { DevelopButton } from './DevelopButton';
import { DevelopMenuItem } from './DevelopMenuItem';
import { useDevelopButtons } from './useDevelopButtons';

export const DevelopCard = () => {
  const { menuItems, pinnedEditors } = useDevelopButtons();
  const isDevelopment = useDeploymentClientState((state) => state.document?.stage === 'development');
  const sshUsernames = useDeploymentClientState((state) => state.document?.sshUsernames || []);
  const [copyTooltip, setCopyTooltip] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });

  // Find devpod entry ssh connect string
  const sshConnectionString =
    sshUsernames.find((s) => s.namespaceName == '' && s.podName == 'dev' && s.containerName == '')?.sshConnectString || '--';

  const items = useMemo(() => menuItems.map((item) => <DevelopMenuItem {...item} />), [menuItems]);

  const handleAddClick = (event: MouseEvent<HTMLButtonElement>) => {
    setMenuPosition({
      x: event.currentTarget.offsetLeft,
      y: event.currentTarget.offsetTop,
    });
    setMenuOpen((state) => !state);
  };

  const handleSshCopy = () => {
    if (navigator?.clipboard) {
      navigator.clipboard.writeText(`ssh ${sshConnectionString}`);
      setCopyTooltip('SSH command copied to the clipboard');
    } else {
      setCopyTooltip('Failed to copy SSH command. Check clipboard permission.');
    }
  };

  useEffect(() => {
    if (!copyTooltip) return;

    const timeout = setTimeout(() => setCopyTooltip(null), 3000);
    return () => clearTimeout(timeout);
  }, [copyTooltip]);

  if (!isDevelopment) return null;

  return (
    <Stack css={cardStyle}>
      <div css={headerStyle}>
        <Typography variant="h6">
          <Code size={24} /> Develop
        </Typography>
      </div>
      <Stack horizontal gap={8} css={actionsStyle}>
        {/* Anchor the dropdown menu to a hidden button to stop it from moving around when buttons change */}
        <DropdownMenu items={items} open={menuOpen} onOpenChange={setMenuOpen}>
          <button
            style={{ left: menuPosition.x, top: menuPosition.y }}
            tabIndex={-1}
            // @ts-ignore
            inert=""
            css={hiddenButtonStyle}
          />
        </DropdownMenu>

        {pinnedEditors.map((item) => (
          <DevelopButton item={item} />
        ))}
        <Button icon={<Plus weight="bold" />} onClick={handleAddClick}></Button>
      </Stack>

      <Typography component="ul" css={listStyle}>
        <Tooltip open={!!copyTooltip} title={copyTooltip} side="right" align="start">
          <li>
            <SshWarningDropdown item="command">
              <a onClick={handleSshCopy}>
                <TerminalWindow /> Connect via SSH <Copy size={14} />
              </a>
            </SshWarningDropdown>
          </li>
        </Tooltip>
        <li>
          <a href="https://docs.diploi.com/guides/remote-development/#other-ides" target="_blank">
            <Info /> Other editors <ArrowSquareOut size={14} />
          </a>
        </li>
      </Typography>
    </Stack>
  );
};

const background = createStackBackgroundUri({
  size: 16,
  gap: 8,
  strokeWidth: 6,
  primary: 'rgba(255, 255, 255, 0.04)',
  secondary: 'rgba(255, 255, 255, 0.02)',
});

const cardStyle = (theme: Theme) => css`
  position: relative;
  margin-bottom: 32px;
  padding: 26px;
  border-radius: ${theme.borderRadius}px;
  border: 2px solid ${theme.color.grey[60]};
  /* background: linear-gradient(
    -5deg,
    ${theme.color.background},
    ${transparentize(theme.type === 'light' ? 0.35 : 0, theme.color.primary[60])} 90%
  ); */
  box-sizing: border-box;
  overflow: hidden;

  &::after {
    position: absolute;
    content: '';
    display: block;
    left: -20px;
    top: -30px;
    width: 120%;
    height: 180%;
    background-image: ${background};
    background-size: 600px;
    transform: rotate(8deg);

    mask-image: linear-gradient(-5deg, transparent 50%, white 90%);

    z-index: 0;
  }
`;

const headerStyle = (theme: Theme) => css`
  z-index: 1;

  > h6 {
    display: flex;
    align-items: center;
    gap: 8px;

    > svg {
      color: ${theme.color.primary[100]};
      translate: 0 -2px;
    }
  }
`;

const actionsStyle = css`
  flex-wrap: wrap;
  z-index: 1;

  > * {
    flex-grow: 0;
  }
`;

const listStyle = css`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  margin: 0;
  padding: 0;
  list-style: none;
  z-index: 1;

  a {
    position: relative;
    text-decoration: none;
    color: inherit;
    user-select: none;
    cursor: pointer;
  }

  li,
  li > a {
    display: flex;
    align-items: center;
    gap: 4px;
  }
`;

const hiddenButtonStyle = css`
  visibility: hidden;
  position: absolute;
  top: 0;
  left: 0;
  padding: 0;
  width: 46px;
  height: 46px;
  pointer-events: none;
`;
